import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ShieldCheck, FileText, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';
import { getMyLegalStatus, acceptLegal, type LegalStatus } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

/**
 * Version-aware policy consent prompt.
 *  - Fires whenever the user's accepted_{doc}_version < current published version.
 *  - "material_change" = true → hard block (can only Accept or Log Out).
 *  - Otherwise = soft block (dismissible "Remind me later", persistent banner elsewhere).
 *  US-focused copy (no EU/GDPR framing) since DutchDate is US-only.
 */
export default function PolicyConsentModal() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<LegalStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;
    getMyLegalStatus()
      .then((res) => setStatus(res.data))
      .catch(() => setStatus(null));
  }, [user]);

  if (!user || !status || !status.needs_accept_any || dismissed) return null;

  const hardBlock = status.hard_block;
  const termsDoc = status.docs.find((d) => d.doc_type === 'terms');
  const privacyDoc = status.docs.find((d) => d.doc_type === 'privacy');
  const changelogs = status.docs
    .filter((d) => d.needs_accept && d.changelog)
    .map((d) => ({ label: d.doc_type === 'terms' ? 'Terms of Service' : 'Privacy Notice', text: d.changelog }));

  const handleAccept = async () => {
    setLoading(true);
    try {
      await acceptLegal('both');
      await refreshUser();
      setStatus(null);
      toast.success('Thanks! Your acceptance has been recorded.');
    } catch {
      toast.error('Could not record your acceptance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await logout(); } finally { navigate('/auth'); }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="policy-consent-modal"
    >
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${hardBlock ? 'bg-amber-50' : 'bg-teal-50'}`}>
            {hardBlock ? <AlertTriangle size={24} className="text-amber-600" /> : <ShieldCheck size={24} className="text-teal-600" />}
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-neutral-900" data-testid="policy-consent-title">
              {hardBlock ? 'Important policy update' : "We've updated our policies"}
            </h2>
            <p className="text-xs text-muted-foreground">
              {hardBlock
                ? 'Please review and accept to continue'
                : 'Please review — it only takes a minute'}
            </p>
          </div>
        </div>

        <div className="bg-neutral-50 rounded-2xl p-4 mb-4 space-y-3">
          <p className="text-sm text-neutral-700 leading-relaxed">
            DutchDate is a US-only service. We've updated our legal documents — the summary is below.
            California, Virginia, Colorado, Connecticut, Utah, and Texas residents retain the right to
            access, correct, and delete their data at any time from <strong>Settings → Privacy</strong>.
          </p>

          {changelogs.length > 0 ? (
            <ul className="space-y-2">
              {changelogs.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-neutral-700">
                  <FileText size={14} className="text-teal-500 mt-0.5 shrink-0" />
                  <span>
                    <strong>{c.label}:</strong> {c.text}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-neutral-700">
                <FileText size={14} className="text-teal-500 mt-0.5 shrink-0" />
                <span>We've refreshed wording for clarity. No change to how your data is used.</span>
              </li>
            </ul>
          )}
        </div>

        <div className="flex gap-3 mb-4 text-xs">
          {termsDoc?.needs_accept && (
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-teal-600 hover:underline" data-testid="open-terms-link">
              Read Terms v{termsDoc.current_version} <ExternalLink size={10} />
            </a>
          )}
          {privacyDoc?.needs_accept && (
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-teal-600 hover:underline" data-testid="open-privacy-link">
              Read Privacy v{privacyDoc.current_version} <ExternalLink size={10} />
            </a>
          )}
        </div>

        <Button
          data-testid="policy-consent-accept"
          onClick={handleAccept}
          disabled={loading}
          className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-heading font-semibold"
        >
          {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
          I accept — continue to DutchDate
        </Button>

        {hardBlock ? (
          <Button
            data-testid="policy-consent-logout"
            onClick={handleLogout}
            variant="outline"
            className="w-full h-11 mt-2 rounded-full text-sm"
          >
            Log out
          </Button>
        ) : (
          <button
            data-testid="policy-consent-later"
            onClick={() => setDismissed(true)}
            className="w-full h-11 mt-2 rounded-full text-sm text-neutral-500 hover:text-neutral-700"
          >
            Remind me later
          </button>
        )}
        <p className="text-[10px] text-center text-muted-foreground mt-3">
          Questions? <a href="mailto:hello@dutchdate.app?subject=Privacy%20Request" className="text-teal-600 hover:underline">hello@dutchdate.app</a>
        </p>
      </div>
    </div>
  );
}
